import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import { useAddManagedBy, useAllAccessUsers, useRemoveManagedBy } from "../../hooks/useAccessManagement";
import { PRIMARY_BTN_SX, SECONDARY_BTN_SX } from "./styles";

// The "Managed by" column: which user(s) this row is managed by (the inverse of the
// dialog's "Manages these users" picker — see models/user.js#manageUsers). Not restricted
// to Admins — manageUsers only affects payment/campaign visibility scoping when its owner
// is an Admin (see routes/accessManagement.js's /managed-by for why), so any user can be
// picked here, matching how the org's existing data already uses it.
//
// The + opens a searchable, checkbox multi-select of the whole org — current managers come
// pre-checked — so you can add several (or remove some) in one go via Confirm, instead of
// one round trip per person. A chip's own "x" still removes just that one manager
// immediately, for the common single-removal case.
export default function ManagedByCell({ user }) {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  // Only fetched once a picker is actually opened (shared across every row's cell via the
  // same cache key, so only the first click on any row triggers the request).
  const { data: allUsersData } = useAllAccessUsers(Boolean(anchorEl));
  const addManagedBy = useAddManagedBy();
  const removeManagedBy = useRemoveManagedBy();

  const managedBy = user.managedBy || [];
  const candidates = (allUsersData?.data || []).filter((candidate) => candidate.id !== user.id);
  const isPending = addManagedBy.isLoading || removeManagedBy.isLoading;

  const filteredCandidates = candidates.filter((candidate) => {
    if (!search.trim()) return true;
    const term = search.trim().toLowerCase();
    return candidate.fullName?.toLowerCase().includes(term) || candidate.email?.toLowerCase().includes(term);
  });

  const openPicker = (e) => {
    setSelectedIds(managedBy.map((manager) => manager.id));
    setSearch("");
    setAnchorEl(e.currentTarget);
  };

  const closePicker = () => setAnchorEl(null);

  const toggleSelected = (managerId) => {
    setSelectedIds((prev) => (prev.includes(managerId) ? prev.filter((id) => id !== managerId) : [...prev, managerId]));
  };

  // Confirmed before it runs — removing a manager quietly narrows what that person can see
  // in Payments/Campaigns (see common/useBaseCondition.js), and a chip's "x" is an easy
  // mis-click. Uses the same SweetAlert confirm the rest of the app uses for destructive
  // actions (see components/payments/PaymentStatus.jsx's handleDecline).
  const handleRemove = async (manager) => {
    const result = await Swal.fire({
      title: "Remove manager?",
      html: `<div style="font-size: 14px; font-family: 'Quicksand'">
          <b>${manager.fullName}</b> will no longer manage <b>${user.fullName}</b>.
        </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#E34C14",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const response = await removeManagedBy.mutateAsync({ id: user.id, managerId: manager.id });
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }
      enqueueSnackbar(`Removed ${manager.fullName}`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not remove manager", { variant: "error" });
    }
  };

  const handleConfirm = async () => {
    const currentIds = managedBy.map((manager) => manager.id);
    const toAdd = selectedIds.filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !selectedIds.includes(id));
    closePicker();

    if (toAdd.length === 0 && toRemove.length === 0) return;

    // Only asked when the batch actually takes someone away — unchecking is easy to do by
    // accident, while pure additions are already explicit enough via Confirm.
    if (toRemove.length > 0) {
      const removedNames = managedBy
        .filter((manager) => toRemove.includes(manager.id))
        .map((manager) => manager.fullName);

      const result = await Swal.fire({
        title: removedNames.length === 1 ? "Remove manager?" : `Remove ${removedNames.length} managers?`,
        html: `<div style="font-size: 14px; font-family: 'Quicksand'">
            <b>${removedNames.join("</b>, <b>")}</b> will no longer manage <b>${user.fullName}</b>.
          </div>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, save changes",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#E34C14",
        cancelButtonColor: "#6b7280",
        reverseButtons: true,
      });

      if (!result.isConfirmed) return;
    }

    // allSettled, not all — each add/remove is its own request that applies (and refreshes
    // the table) independently the moment it resolves, so a sibling failing must not be
    // reported as if nothing happened. This reports exactly what did and didn't take.
    const settled = await Promise.allSettled([
      ...toAdd.map((managerId) => addManagedBy.mutateAsync({ id: user.id, managerId })),
      ...toRemove.map((managerId) => removeManagedBy.mutateAsync({ id: user.id, managerId })),
    ]);
    const failedCount = settled.filter(
      (result) => result.status === "rejected" || result.value?.success === false
    ).length;

    if (failedCount === 0) {
      enqueueSnackbar("Managed by updated", { variant: "success" });
    } else if (failedCount === settled.length) {
      const firstError = settled.find((result) => result.status === "rejected" || result.value?.success === false);
      const message =
        firstError.status === "rejected"
          ? firstError.reason?.response?.data?.message
          : firstError.value?.message;
      enqueueSnackbar(message || "Could not update managers", { variant: "error" });
    } else {
      enqueueSnackbar(`${settled.length - failedCount} of ${settled.length} changes saved — ${failedCount} failed`, {
        variant: "warning",
      });
    }
  };

  return (
    <Box
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}
      onClick={(e) => e.stopPropagation()}
    >
      {managedBy.length === 0 && (
        <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 13, color: "#9CA3AF" }}>
          —
        </Typography>
      )}
      {managedBy.map((manager) => (
        <Chip
          key={manager.id}
          size="small"
          label={manager.fullName}
          onDelete={() => handleRemove(manager)}
          disabled={isPending}
          sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 11 }}
        />
      ))}

      <Tooltip title="Manage who oversees this user">
        <span>
          <IconButton size="small" onClick={openPicker} disabled={isPending}>
            <AddIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: { borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 8px 28px rgba(0,0,0,0.10)" },
        }}
      >
        {/* 300px overflows a 320px phone once the popover's own margin is counted, so it
            gives way to the viewport on the narrowest screens. */}
        <Box sx={{ width: { xs: "min(300px, calc(100vw - 32px))", sm: 300 } }}>
          <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
            <Typography
              sx={{
                fontFamily: "Quicksand",
                fontWeight: 800,
                fontSize: 11,
                color: "#9CA3AF",
                textTransform: "uppercase",
                letterSpacing: 1,
                mb: 1,
              }}
            >
              Managed by
            </Typography>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontFamily: "Quicksand",
                  fontWeight: 600,
                  fontSize: 13,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Divider />

          <List dense sx={{ maxHeight: 280, overflowY: "auto", py: 0.5 }}>
            {filteredCandidates.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 13, color: "#9CA3AF" }}>
                  No matching users
                </Typography>
              </Box>
            ) : (
              filteredCandidates.map((candidate) => (
                <ListItemButton key={candidate.id} onClick={() => toggleSelected(candidate.id)} dense>
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={selectedIds.includes(candidate.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={candidate.fullName}
                    secondary={candidate.email}
                    primaryTypographyProps={{ sx: { fontFamily: "Quicksand", fontWeight: 700, fontSize: 13, color: "#1C1C2E" } }}
                    secondaryTypographyProps={{ sx: { fontFamily: "Quicksand", fontWeight: 600, fontSize: 11, color: "#9CA3AF" } }}
                  />
                </ListItemButton>
              ))
            )}
          </List>

          <Divider />

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 1 }}>
            <Button
              size="small"
              onClick={closePicker}
              disabled={isPending}
              sx={SECONDARY_BTN_SX}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleConfirm}
              disabled={isPending}
              sx={PRIMARY_BTN_SX}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}
